import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import SectionCard from './components/SectionCard';
import FileUploadArea from './components/FileUploadArea';
import DocumentCard from './components/DocumentCard';
import DocumentPreviewModal from './components/DocumentPreviewModal';
import AchievementTimeline from './components/AchievementTimeline';
import PersonalInfoForm from './components/PersonalInfoForm';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import DocumentMetadataModal from './components/DocumentMetadataModal';
import EditDocumentModal from './components/EditDocumentModal';
import SaveToast from './components/SaveToast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { persistDocumentMetadata, fetchUserDocuments, deleteDocument, savePersonalInfo, fetchPersonalInfo } from './lib/documentService';

const PortfolioBuilder = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [editDocumentModal, setEditDocumentModal] = useState({ isOpen: false, document: null });
  const [pendingFiles, setPendingFiles] = useState(null);
  const [pendingSection, setPendingSection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ visible: false, message: '', variant: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, documentId: null, sectionId: null, filePath: null });

  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    grade: '',
    country: '',
    city: '',
    school: ''
  });

  const [formErrors, setFormErrors] = useState({});

  const [documents, setDocuments] = useState({
    academic: [],
    achievements: [],
    volunteering: [],
    skills: [],
    extracurricular: []
  });

  // Load user and documents on mount
  useEffect(() => {
    const loadUserAndDocuments = async () => {
      try {
        const {
          data: { user: currentUser },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError || !currentUser) {
          console.error('User not authenticated');
          navigate('/login');
          return;
        }

        setUser(currentUser);

        // Fetch documents from Supabase
        const fetchedDocuments = await fetchUserDocuments(currentUser.id);
        setDocuments((prev) => ({
          ...prev,
          ...fetchedDocuments,
        }));

        // Fetch personal info from Supabase
        const fetchedPersonalInfo = await fetchPersonalInfo(currentUser.id);
        if (fetchedPersonalInfo) {
          setPersonalInfo((prev) => ({
            ...prev,
            ...fetchedPersonalInfo,
          }));
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserAndDocuments();
  }, [navigate]);

  const personalRequiredFields = ['firstName', 'lastName', 'email'];
  const completedPersonalFields = personalRequiredFields.filter(
    (field) => personalInfo?.[field]?.trim()
  ).length;
  const personalCompletion = personalRequiredFields.length
    ? Math.round((completedPersonalFields / personalRequiredFields.length) * 100)
    : 0;

  const sections = [
    {
      id: 'personal',
      title: 'Personal Information',
      description: 'Basic details and contact information',
      icon: 'User',
      isComplete: personalCompletion === 100
    },
    {
      id: 'academic',
      title: 'Academic Records',
      description: 'Diplomas, transcripts, and certificates',
      icon: 'GraduationCap',
      isComplete: documents?.academic?.length >= 2
    },
    {
      id: 'achievements',
      title: 'Personal Projects',
      description: 'Your personal and academic projects',
      icon: 'Briefcase',
      isComplete: documents?.achievements?.length >= 1
    },
    {
      id: 'volunteering',
      title: 'Volunteering Experience',
      description: 'Community service and volunteer work',
      icon: 'Heart',
      isComplete: documents?.volunteering?.length >= 1
    },
    {
      id: 'skills',
      title: 'Skills & Languages',
      description: 'Technical skills and language proficiency',
      icon: 'Code',
      isComplete: documents?.skills?.length >= 1
    },
    {
      id: 'extracurricular',
      title: 'Extracurricular Activities',
      description: 'Clubs, sports, and other activities',
      icon: 'Users',
      isComplete: documents?.extracurricular?.length >= 1
    }
  ];

  const handleFileSelect = (sectionId, files) => {
    // Open metadata modal instead of directly adding documents
    setPendingFiles(files);
    setPendingSection(sectionId);
    setMetadataModalOpen(true);
  };

  const handleMetadataConfirm = async (files, metadata) => {
    if (!user) {
      alert('User not authenticated');
      return;
    }

    try {
      setIsSaving(true);
      const newDocuments = [];

      // Persist each file with metadata
      for (let i = 0; i < files.length; i++) {
        const persistedDoc = await persistDocumentMetadata(
          user.id,
          pendingSection,
          files[i],
          metadata[i]
        );
        newDocuments.push(persistedDoc);
      }

      // Update local state
      setDocuments((prev) => ({
        ...prev,
        [pendingSection]: [...prev?.[pendingSection], ...newDocuments]
      }));

      setMetadataModalOpen(false);
      setPendingFiles(null);
      setPendingSection(null);
      setLastSaved(Date.now());
      setToast({ visible: true, message: `Uploaded ${newDocuments.length} document${newDocuments.length === 1 ? '' : 's'}`, variant: 'success' });
    } catch (err) {
      console.error('Error saving documents:', err);
      setToast({ visible: true, message: 'Something unexpected occurred while uploading.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDocument = async (sectionId, documentId, filePath) => {
    setDeleteDialog({
      isOpen: true,
      documentId,
      sectionId,
      filePath
    });
  };

  const confirmDeleteDocument = async () => {
    const { documentId, sectionId, filePath } = deleteDialog;
    setIsSaving(true);
    
    try {
      await deleteDocument(documentId, filePath);

      setDocuments((prev) => ({
        ...prev,
        [sectionId]: prev?.[sectionId]?.filter((doc) => doc?.id !== documentId)
      }));

      setLastSaved(Date.now());
      setToast({ visible: true, message: 'Document removed from portfolio', variant: 'success' });
      setDeleteDialog({ isOpen: false, documentId: null, sectionId: null, filePath: null });
    } catch (err) {
      console.error('Error deleting document:', err);
      setToast({ visible: true, message: 'Something unexpected occurred while deleting.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditDocument = (document) => {
    setEditDocumentModal({ isOpen: true, document });
  };

  const handleSaveEditedDocument = async (updatedDocument) => {
    setIsSaving(true);
    try {
      // Find the section containing this document
      let sectionId = null;
      for (const [key, docs] of Object.entries(documents)) {
        if (docs.some(doc => doc.id === updatedDocument.id)) {
          sectionId = key;
          break;
        }
      }

      if (!sectionId) {
        throw new Error('Document section not found');
      }

      // Update in Supabase
      const { error } = await supabase
        .from('portfolio_documents')
        .update({
          title: updatedDocument.title,
          description: updatedDocument.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedDocument.id);

      if (error) throw error;

      // Update local state
      setDocuments((prev) => ({
        ...prev,
        [sectionId]: prev[sectionId].map((doc) =>
          doc.id === updatedDocument.id
            ? { ...doc, title: updatedDocument.title, description: updatedDocument.description }
            : doc
        )
      }));

      setLastSaved(Date.now());
      setToast({ visible: true, message: 'Document updated successfully!', variant: 'success' });
      setEditDocumentModal({ isOpen: false, document: null });
    } catch (err) {
      console.error('Error updating document:', err);
      setToast({ visible: true, message: 'Error updating document. Please try again.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo((prev) => ({
      ...prev,
      [field]: value
    }));

    if (formErrors?.[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSavePersonalInfo = async () => {
    try {
      setIsSaving(true);
      await savePersonalInfo(user.id, personalInfo);
      setLastSaved(Date.now());
      setToast({ visible: true, message: 'Personal information saved successfully!', variant: 'success' });
    } catch (err) {
      console.error('Error saving personal info:', err);
      setToast({ visible: true, message: 'Error: Something unexpected occurred.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Remove autosave effect - now manual save only
  useEffect(() => {
    // Cleanup if needed
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen app-shell flex items-center justify-center">
        <Header />
        <div className="text-center">
          <p className="text-muted-foreground">Loading your portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-shell">
      <Helmet>
        <title>Portfolio - UniSteps</title>
        <meta name="description" content="Build your academic portfolio and showcase your achievements" />
      </Helmet>
      
      <Header />
      <main className="pt-20 sm:pt-24 pb-20 sm:pb-14 safe-bottom mobile-scroll">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 space-y-6 sm:space-y-8">
          <div className="surface-card rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 overflow-hidden relative lifted fade-up">
            <div className="absolute inset-0 tile-grid opacity-60" aria-hidden="true" />
            <div className="relative z-10 flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
              <div className="space-y-2 sm:space-y-3">
                <p className="text-xs sm:text-sm uppercase tracking-[0.25em] sm:tracking-[0.3em] text-muted-foreground">Portfolio workspace</p>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold headline-serif text-foreground">Create an academic story that stands out</h1>
                <p className="text-sm sm:text-base text-muted-foreground max-w-3xl">Structure achievements, upload documents, and track completeness with a calm, focused layout.</p>
              </div>
              {/* right side intentionally empty to avoid crowding; autosave is anchored bottom-right */}
            </div>
            {/* Autosave anchored to bottom-right of the hero box - hidden on small mobile */}
            <div className="hidden sm:block absolute bottom-4 right-4 z-10">
              <div className="glass-panel glass-stroke rounded-xl px-3 sm:px-4 py-2 sm:py-3 min-w-[200px] sm:min-w-[240px] flex items-center justify-between text-xs sm:text-sm shadow-lg">
                <span className="text-muted-foreground">Autosave</span>
                <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 space-y-3 sm:space-y-4">
              {sections?.map((section) =>
                <SectionCard
                  key={section?.id}
                  title={section?.title}
                  description={section?.description}
                  icon={section?.icon}
                  isComplete={section?.isComplete}
                  isExpanded={expandedSection === section?.id}
                  onExpand={() => setExpandedSection(
                    expandedSection === section?.id ? null : section?.id
                  )}>

                  {section?.id === 'personal' ?
                    <PersonalInfoForm
                      formData={personalInfo}
                      onChange={handlePersonalInfoChange}
                      errors={formErrors}
                      onSave={handleSavePersonalInfo}
                      isSaving={isSaving} /> :

                    <>
                      {documents?.[section?.id]?.length > 0 &&
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                          {documents?.[section?.id]?.map((doc) =>
                            <DocumentCard
                              key={doc?.id}
                              document={doc}
                              onPreview={setPreviewDocument}
                              onEdit={handleEditDocument}
                              onDelete={() => handleDeleteDocument(section?.id, doc?.id, doc?.url)} />
                          )}
                        </div>
                      }

                      <FileUploadArea
                        onFileSelect={(files) => handleFileSelect(section?.id, files)}
                        label={`Upload ${section?.title}`}
                        maxSizeMB={10}
                        isLoading={isSaving} />
                    </>
                  }
                </SectionCard>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 sm:pt-6">
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                  onClick={() => navigate('/student-dashboard')}>

                  Back to Dashboard
                </Button>
                {/* Mobile autosave indicator */}
                <div className="sm:hidden glass-panel glass-stroke rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Autosave</span>
                  <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} />
                </div>
              </div>
            </div>

            <div className="lg:col-span-1 order-first lg:order-last">
              <div className="surface-card rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 lifted">
                <AchievementTimeline
                  sections={sections}
                  documents={documents} />
              </div>
            </div>
          </div>
        </div>
      </main>
      {previewDocument &&
        <DocumentPreviewModal
          document={previewDocument}
          onClose={() => setPreviewDocument(null)} />
      }

      {metadataModalOpen && pendingFiles && (
        <DocumentMetadataModal
          files={pendingFiles}
          sectionTitle={sections?.find((s) => s?.id === pendingSection)?.title}
          onConfirm={handleMetadataConfirm}
          onCancel={() => {
            setMetadataModalOpen(false);
            setPendingFiles(null);
            setPendingSection(null);
          }}
        />
      )}

      {editDocumentModal.isOpen && editDocumentModal.document && (
        <EditDocumentModal
          document={editDocumentModal.document}
          onConfirm={handleSaveEditedDocument}
          onCancel={() => setEditDocumentModal({ isOpen: false, document: null })}
        />
      )}

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, documentId: null, sectionId: null, filePath: null })}
        onConfirm={confirmDeleteDocument}
        title="Delete Document?"
        message="This document will be permanently removed from your portfolio. This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={isSaving}
      />

      <SaveToast
        message={toast.message}
        variant={toast.variant}
        visible={toast.visible}
        offsetTop={132}
        offsetRight={24}
        onClose={() => setToast((t) => ({ ...t, visible: false }))}
      />
    </div>
  );
};

export default PortfolioBuilder;
